import React, { useEffect, useState } from 'react';
import { getUsers, createUser, updateUser, deleteUser, getFactories } from '../services/api';
import toast from 'react-hot-toast';

const roleLabels = { super_admin: 'Super Admin', manager: 'Διαχειριστής', operator: 'Χειριστής' };
const roleBadge = { super_admin: 'badge-red', manager: 'badge-orange', operator: 'badge-blue' };
const emptyForm = { username: '', full_name: '', password: '', role: 'operator', factory_ids: [] };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([getUsers(), getFactories()])
      .then(([uRes, fRes]) => { setUsers(uRes.data); setFactories(fRes.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditUser(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (u) => {
    setEditUser(u);
    setForm({ username: u.username, full_name: u.full_name, password: '', role: u.role, factory_ids: u.factories?.map(f => f.id) || [] });
    setShowModal(true);
  };

  const toggleFactory = (fid) => {
    setForm(f => ({
      ...f,
      factory_ids: f.factory_ids.includes(fid) ? f.factory_ids.filter(id => id !== fid) : [...f.factory_ids, fid]
    }));
  };

  const handleSave = async () => {
    if (!form.full_name || !form.role) return toast.error('Συμπληρώστε όλα τα υποχρεωτικά πεδία');
    if (!editUser && !form.username) return toast.error('Απαιτείται όνομα χρήστη');
    if (!editUser && !form.password) return toast.error('Απαιτείται κωδικός');
    setSaving(true);
    try {
      const payload = { ...form };
      if (editUser && !payload.password) delete payload.password;
      if (editUser) {
        await updateUser(editUser.id, payload);
        toast.success('Ο χρήστης ενημερώθηκε');
      } else {
        await createUser(payload);
        toast.success('Ο χρήστης δημιουργήθηκε');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (u) => {
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? 'Ο χρήστης απενεργοποιήθηκε' : 'Ο χρήστης ενεργοποιήθηκε');
      load();
    } catch { toast.error('Σφάλμα'); }
  };

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>Χρήστες</h2>
            <p>Διαχείριση λογαριασμών και δικαιωμάτων</p>
          </div>
          <button className="btn btn-primary" onClick={openCreate}>+ Νέος Χρήστης</button>
        </div>
      </div>
      <div className="page-body">
        <div className="card">
          {loading ? (
            <div className="page-loading"><span className="loading-spinner dark" /></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Χρήστης</th><th>Username</th><th>Ρόλος</th><th>Εργοστάσια</th><th>Κατάσταση</th><th style={{ textAlign: 'right' }}>Ενέργειες</th></tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.full_name}</strong></td>
                      <td style={{ color: 'var(--gray-500)' }}>@{u.username}</td>
                      <td><span className={`badge ${roleBadge[u.role]}`}>{roleLabels[u.role]}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {u.factories?.map(f => f.name).join(', ') || '—'}
                      </td>
                      <td><span className={`badge ${u.is_active ? 'badge-green' : 'badge-gray'}`}>{u.is_active ? 'Ενεργός' : 'Ανενεργός'}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>✏️</button>
                          <button className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggle(u)}>
                            {u.is_active ? 'Απενεργ.' : 'Ενεργοπ.'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editUser ? 'Επεξεργασία Χρήστη' : 'Νέος Χρήστης'}</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Ονοματεπώνυμο *</label>
                  <input className="form-input" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <input className="form-input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} disabled={!!editUser} />
                </div>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">{editUser ? 'Νέος Κωδικός (κενό = χωρίς αλλαγή)' : 'Κωδικός *'}</label>
                  <input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ρόλος *</label>
                  <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                    <option value="operator">Χειριστής</option>
                    <option value="manager">Διαχειριστής</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Εργοστάσια</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {factories.map(f => (
                    <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 12px', border: `1.5px solid ${form.factory_ids.includes(f.id) ? 'var(--blue-dark)' : 'var(--gray-200)'}`, borderRadius: 6, fontSize: 13, background: form.factory_ids.includes(f.id) ? 'var(--blue-light)' : 'white', color: form.factory_ids.includes(f.id) ? 'var(--blue-dark)' : 'var(--gray-700)' }}>
                      <input type="checkbox" checked={form.factory_ids.includes(f.id)} onChange={() => toggleFactory(f.id)} style={{ display: 'none' }} />
                      {form.factory_ids.includes(f.id) ? '✓ ' : ''}{f.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Ακύρωση</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : (editUser ? 'Αποθήκευση' : 'Δημιουργία')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
